import React, { useState, useEffect, useRef, useMemo } from 'react';
import './HeroSection.css';

const HeroSection = () => {
  const texts = useMemo(() => [
    "une Alternance pour être Designer UX/UI",
    "un maximum d'opportunités pour évoluer", 
    "un projet innovant à développer"
  ], []);

  const [displayText, setDisplayText] = useState('');
  const [textIndex, setTextIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0 });

  // Animation de frappe
  useEffect(() => {
    const typingSpeed = 80;
    const deletingSpeed = 40;
    const pauseTime = 1500;

    const timeout = setTimeout(() => {
      const currentText = texts[textIndex];
      
      if (!isDeleting && charIndex < currentText.length) {
        setDisplayText(currentText.substring(0, charIndex + 1));
        setCharIndex(charIndex + 1);
      } else if (isDeleting && charIndex > 0) {
        setDisplayText(currentText.substring(0, charIndex - 1));
        setCharIndex(charIndex - 1);
      } else if (!isDeleting && charIndex === currentText.length) {
        setTimeout(() => setIsDeleting(true), pauseTime);
      } else if (isDeleting && charIndex === 0) {
        setIsDeleting(false);
        setTextIndex((textIndex + 1) % texts.length);
      }
    }, isDeleting ? deletingSpeed : typingSpeed);

    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, textIndex, texts]);

  // Système de particules
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    // Redimensionner le canvas
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Classe Particule
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 2;
        this.vy = (Math.random() - 0.5) * 2;
        this.radius = Math.random() * 3 + 1;
        this.baseRadius = this.radius;
        this.opacity = Math.random() * 0.8 + 0.2;
        this.baseOpacity = this.opacity;
        this.color = this.getRandomColor();
      }

      getRandomColor() {
        const colors = [
          '#ffcbb3',
          '#811453',
          '#f59e0b',
          '#d58490', 
          '#ef4444'  
        ];
        return colors[Math.floor(Math.random() * colors.length)];
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        // Rebond sur les bords
        if (this.x <= 0 || this.x >= canvas.width) this.vx *= -1;
        if (this.y <= 0 || this.y >= canvas.height) this.vy *= -1;

        // Garder dans les limites
        this.x = Math.max(0, Math.min(canvas.width, this.x));
        this.y = Math.max(0, Math.min(canvas.height, this.y));

        // Interaction avec la souris
        const dx = mouseRef.current.x - this.x;
        const dy = mouseRef.current.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 100) {
          // Plus proche = plus d'effet
          const intensity = (100 - distance) / 100;
          this.radius = this.baseRadius + intensity * 4;
          this.opacity = Math.min(1, this.baseOpacity + intensity * 0.6);
        } else {
          // Retour à la normale progressivement
          this.radius = this.baseRadius;
          this.opacity = this.baseOpacity;
        }
      }

      draw() {
        ctx.save();
        ctx.globalAlpha = this.opacity;
        
        // Particule principale
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fill();
        
        // Effet de glow si la particule est agrandie
        if (this.radius > this.baseRadius) {
          ctx.globalAlpha = this.opacity * 0.3;
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.radius * 2, 0, Math.PI * 2);
          ctx.fill();
        }
        
        ctx.restore();
      }
    }

    // Créer les particules
    const particleCount = 250; 
    particlesRef.current = [];
    for (let i = 0; i < particleCount; i++) {
      particlesRef.current.push(new Particle());
    }

    // Animation
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particlesRef.current.forEach(particle => {
        particle.update();
        particle.draw();
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };

    // Gestion de la souris
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('mousemove', handleMouseMove);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <section className="hero">
      <canvas ref={canvasRef} className="hero-canvas" />
      <div className="hero-header">
        <div className="hero-logo">
          <img 
            src={`${process.env.PUBLIC_URL}/HeroSection/logo.png`} 
            alt="Tatiana Fuhrer - Développeuse Web" 
            className="hero-logo-image" 
          />
        </div>
        <nav className="hero-nav">
          <ul>
            <li><a href="#about">À propos</a></li>
            <li><a href="#compétences">Compétences</a></li>
            <li><a href="#portfolio">Portfolio</a></li>
            <li><a href="#contact">Contact</a></li>
          </ul>
        </nav>
      </div>
      <div className="hero-content">
        <h2>Bonjour !</h2>
        <p>
          Je recherche actuellement{' '}
          <span className="typing-text">
            {displayText}
            <span className="cursor">|</span>
          </span>
        </p>
      </div>
    </section>
  );
};

export default HeroSection;
